import { CustomModal } from "@cbex/ui/modal";
import {
  Stack,
  Button,
  Typography,
  Box,
  Paper,
  Divider,
  useTheme,
  alpha,
  IconButton,
  Link,
  FormHelperText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from "@mui/material";
import { useTranslations } from "next-intl";
import React, { useState, useRef } from "react";
import {
  CbexPortalCandidatesByMomentIdRow,
  PlanningActivityType,
} from "@cbex/data-access";
import readXlsxFile from "read-excel-file";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";

interface ImportExcelModalProps {
  totalAvailableSeats: number;
  modalOpen: boolean;
  handleClose: () => void;
  handleCloseWithData: (data: CbexPortalCandidatesByMomentIdRow[]) => void;
  activityTypeOptions: Array<PlanningActivityType>;
}

export const ImportExcelModal = ({
  totalAvailableSeats,
  handleClose,
  modalOpen,
  handleCloseWithData,
  activityTypeOptions,
}: ImportExcelModalProps) => {
  const [modalState] = React.useState(modalOpen);
  const t = useTranslations();
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [parsedData, setParsedData] = useState<
    CbexPortalCandidatesByMomentIdRow[]
  >([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedActivityTypeID, setSelectedActivityTypeID] =
    useState<string>("");

  // Define the schema for the Excel file
  const schema = {
    Achternaam: {
      prop: "lastName",
      type: String,
      required: true,
    },
    Voorletters: {
      prop: "initials",
      type: String,
      required: true,
    },
    Tussenvoegsel: {
      prop: "infix",
      type: String,
    },
    Geboortedatum: {
      prop: "dateOfBirth",
      type: Date,
      required: true,
    },
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    processFile(file);
  };

  const processFile = async (file: File) => {
    setSelectedFile(file);
    setErrorMessage("");

    try {
      const { rows, errors } = await readXlsxFile(file, { schema });

      if (errors && errors.length > 0) {
        setErrorMessage(t("feedback.invalidExcelFormat"));
        setParsedData([]);
        return;
      }

      if (!rows || rows.length === 0) {
        setErrorMessage(t("feedback.noValidDataToImport"));
        setParsedData([]);
        return;
      }
      // Map the parsed data to the expected format
      const candidates: CbexPortalCandidatesByMomentIdRow[] = rows.map(
        (row) =>
          ({
            lastName: row.lastName,
            initials: row.initials,
            infix: row.infix,
            dateOfBirth: row.dateOfBirth,
            activityTypeID: selectedActivityTypeID || undefined,
          } as CbexPortalCandidatesByMomentIdRow)
      );

      if (candidates.length > totalAvailableSeats) {
        setErrorMessage(
          `${t("feedback.tooManyCandidates")}. ${t(
            "labels.seatsRemaining"
          )} : ${totalAvailableSeats}`
        );
        setParsedData([]);
        return;
      }

      setParsedData(candidates);
    } catch (error) {
      console.error("Error parsing Excel file:", error);
      setErrorMessage(t("feedback.excelParsingError"));
      setParsedData([]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (
        file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        processFile(file);
      } else {
        setErrorMessage(t("feedback.onlyExcelFilesAllowed"));
      }
    }
  };

  const handleClick = () => {
    if (!selectedFile) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFile(null);
    setParsedData([]);
    setErrorMessage("");
    setSelectedActivityTypeID("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImport = () => {
    if (parsedData.length > 0 && selectedActivityTypeID) {
      // Update all candidates with selected activity type before import
      const candidatesWithActivity = parsedData.map((candidate) => ({
        ...candidate,
        activityTypeID: selectedActivityTypeID,
      }));
      handleCloseWithData(candidatesWithActivity);
    } else if (!selectedActivityTypeID) {
      setErrorMessage(t("feedback.selectActivityType"));
    } else {
      setErrorMessage(t("feedback.noValidDataToImport"));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleActivityChange = (event: SelectChangeEvent<string>) => {
    const activityTypeID = event.target.value as string;
    setSelectedActivityTypeID(activityTypeID);

    // Update the candidates with the new activity type
    if (parsedData.length > 0) {
      const updatedData = parsedData.map((candidate) => ({
        ...candidate,
        activityTypeID,
      }));
      setParsedData(updatedData);
    }
  };

  return (
    <Stack id="ImportExcelModal">
      <CustomModal
        modalTitle={t("common.importExcel")}
        openModal={modalState}
        closeHandler={handleClose}
      >
        <Stack direction="column" spacing={3} minWidth={400}>
          {!selectedFile ? (
            <>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
              >
                <DownloadIcon fontSize="small" color="primary" />
                <Link
                  href="https://storage.googleapis.com/cbex/Template_Kandidaten.xltx"
                  download
                  variant="body2"
                  color="primary"
                  underline="hover"
                  sx={{ cursor: "pointer" }}
                >
                  {t("labels.downloadTemplate")}
                </Link>
              </Stack>

              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  overflow: "hidden",
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
              >
                <Paper
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 4,
                    border: `2px dashed ${
                      isDragging
                        ? theme.palette.primary.main
                        : alpha(theme.palette.divider, 0.5)
                    }`,
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: isDragging
                      ? alpha(theme.palette.primary.light, 0.1)
                      : alpha(theme.palette.background.paper, 0.9),
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.light, 0.1),
                      borderColor: theme.palette.primary.light,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      textAlign: "center",
                    }}
                  >
                    <input
                      type="file"
                      accept=".xlsx"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      style={{
                        clip: "rect(0 0 0 0)",
                        clipPath: "inset(50%)",
                        height: 1,
                        overflow: "hidden",
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        whiteSpace: "nowrap",
                        width: 1,
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 2,
                        p: 2,
                        borderRadius: "50%",
                      }}
                    >
                      <CloudUploadIcon color="primary" sx={{ fontSize: 48 }} />
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {t("labels.uploadExcel")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {isDragging
                        ? t("labels.dropFileHere")
                        : t("labels.dragOrClickToUpload")}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </>
          ) : (
            <Stack spacing={3}>
              <Paper sx={{ padding: 2, width: "100%" }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <InsertDriveFileIcon color="primary" />
                    <Stack>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatFileSize(selectedFile.size)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <IconButton
                    onClick={handleRemoveFile}
                    size="small"
                    aria-label={t("labels.removeFile")}
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
                <Divider sx={{ my: 2 }} />

                {errorMessage ? (
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ fontWeight: "medium", p: 3 }}
                  >
                    {errorMessage}
                  </Typography>
                ) : parsedData.length > 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: "medium", p: 3 }}
                  >
                    {`${t("feedback.foundCandidates")} : ${parsedData.length}`}
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 3 }}
                  >
                    {`${t("feedback.processingFile")}...`}
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                {parsedData.length > 0 && (
                  <Box sx={{ width: "100%" }}>
                    <FormControl
                      variant="filled"
                      fullWidth
                      error={!selectedActivityTypeID}
                    >
                      <InputLabel id="activity-type-select-label">
                        {t("labels.activityType")}
                      </InputLabel>
                      <Select
                        labelId="activity-type-select-label"
                        id="activity-type-select"
                        value={selectedActivityTypeID}
                        onChange={(value) => handleActivityChange(value)}
                        label={t("labels.activityType")}
                        required
                      >
                        {activityTypeOptions.map((activity) => (
                          <MenuItem
                            key={activity.activityTypeID}
                            value={activity.activityTypeID}
                          >
                            {activity.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {!selectedActivityTypeID && (
                        <FormHelperText>
                          {t("feedback.required")}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                )}
              </Paper>
            </Stack>
          )}

          <Stack direction="row" justifyContent="center" spacing={2}>
            <Button onClick={handleClose} variant="outlined">
              {t("labels.cancel")}
            </Button>
            <Button
              onClick={handleImport}
              variant="contained"
              disabled={parsedData.length === 0 || !selectedActivityTypeID}
            >
              {t("labels.import")}
            </Button>
          </Stack>
        </Stack>
      </CustomModal>
    </Stack>
  );
};

export default ImportExcelModal;
